import { readonly, shallowRef } from "vue";

/**
 * A BARRA DO TOPO — o estado dela, sem desenho nenhum.
 *
 * A V2 tinha uma barra azul no alto da página que acendia com qualquer pedido em
 * voo e ficava vermelha quando o último falhava (`useAxiosLoadingBar`, com os
 * interceptors do axios). A V3 nasceu sem ela: quem clicava num botão cujo pedido
 * demorava não via nada até a tela mudar, e um erro silencioso de uma consulta de
 * fundo passava sem rastro nenhum.
 *
 * Aqui mora só a CONTAGEM. Quem a alimenta são as portas por onde o trabalho
 * real passa — `send()` do cliente HTTP, a navegação do router, o carregamento
 * preguiçoso das telas, a abertura dos streams e os uploads —, e quem a desenha é
 * a primitiva `LoadingBar`, que o shell liga a este estado. A primitiva não
 * importa isto porque `design/` não conhece `shared/http`; e isto não conhece a
 * primitiva porque um contador não precisa saber que cor tem.
 *
 * POR FONTE, e não um contador só: cada fonte fecha o que ela abriu. Um `end`
 * de navegação nunca desconta um pedido HTTP, e um bug de contagem numa porta
 * fica restrito a ela em vez de apagar (ou prender) a barra das outras.
 *
 * Sem relógio permanente: o único temporizador é o de 1,2 s que devolve o
 * vermelho ao repouso, armado uma vez por falha.
 */
export type LoadSource = "http" | "route" | "stream";
export type LoadingState = "idle" | "loading" | "error";

/** Quanto tempo o vermelho fica depois de a última coisa em voo falhar. */
export const ERROR_HOLD_MS = 1200;

const inFlight: Record<LoadSource, number> = { http: 0, route: 0, stream: 0 };
/** Alguma coisa desta RODADA (desde que o contador saiu do zero) falhou. */
let failedThisRound = false;
let errorTimer: ReturnType<typeof setTimeout> | undefined;

const state = shallowRef<LoadingState>("idle");
export const loadingState = readonly(state);

function total(): number {
	return inFlight.http + inFlight.route + inFlight.stream;
}

/**
 * Abre um trabalho em voo e devolve quem o fecha.
 *
 * O fecho é IDEMPOTENTE, e é isso que deixa cada porta chamá-lo tanto no caminho
 * feliz quanto num `finally` sem contar duas vezes — um contador que desconta a
 * mais apaga a barra com outro pedido ainda no ar.
 *
 * `end(true)` marca a rodada como falhada; o vermelho só aparece quando TUDO
 * zera. Um pedido que falha no meio de outros três ainda em voo não pinta a barra
 * a meio caminho — o mesmo que a V2 fazia, e o que se lê como "acabou, e algo
 * deu errado".
 */
export function beginLoading(source: LoadSource): (failed?: boolean) => void {
	if (total() === 0) {
		// Uma rodada nova: o vermelho da anterior não é desta. Um start durante o
		// vermelho volta a azul na hora.
		failedThisRound = false;
		clearTimeout(errorTimer);
	}
	inFlight[source] += 1;
	state.value = "loading";

	let open = true;
	return (failed = false) => {
		if (!open) return;
		open = false;
		inFlight[source] = Math.max(0, inFlight[source] - 1);
		if (failed) failedThisRound = true;
		if (total() > 0) return;
		if (!failedThisRound) {
			state.value = "idle";
			return;
		}
		state.value = "error";
		errorTimer = setTimeout(() => {
			if (state.value === "error") state.value = "idle";
		}, ERROR_HOLD_MS);
	};
}

/** Para os specs: volta ao repouso sem nada em voo. */
export function resetLoadingForTests(): void {
	inFlight.http = 0;
	inFlight.route = 0;
	inFlight.stream = 0;
	failedThisRound = false;
	clearTimeout(errorTimer);
	state.value = "idle";
}
